import * as React from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Container } from '../../../layout/Container';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';

import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { BaseHeroContent, Hero } from '../../../ui/hero/Index';

import { Textarea } from '../../../ui/form/Textarea';
import { MAIL_MUTATION } from '../../../../../client/__graphql__/mutations/mail';
import { Mail, MailVariables } from '../../../../__types__/generated/Mail';
import { BackgroundLoad } from '../../../layout/BackgroundLoad';

const Fremsnakk: React.FC = () => {
  const location = useLocation();
  const { routeConfig } = React.useContext(SiteContext);

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const [mail, { error }] = useMutation<Mail, MailVariables>(MAIL_MUTATION, {
    onCompleted: ({ lead }) => {
      setLoading(false);
      if (lead.mail?.success) {
        setPosted(true);
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
      /*       {
        type: 'textarea',
        name: 'comment',
        placeholder: 'Eks. kommentar',
        value: '',
        label: 'Kommentar'
      } */
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      setLoading(true);

      mail({
        variables: {
          input: {
            ...preparedFields,
            referer: `${location.pathname}${location.search}`,
            leadType: 'EPOST',
            mailType: 124
          }
        }
      });
    }
  });

  return (
    <>
      <Seo
        title="Privatmegleren - Me Kjenne Nordstrand"
        description="Vi har lang erfaring når det kommer til boligsalg i området, og vi vet hva som skal til for å oppnå en høyest mulig pris"
        image="https://cdn.reeltime.no/pm_assets/kampanjer/sandnes/Me_kjenne_Sandnes_toppbilde_Gradient.jpg"
        url={location.pathname}
      />
      <Container className="padding-top" style={{ ...pageFadeIn }}>
        <Heading tag="h1" center={true}>
          Vi kjenner Nordstrand
        </Heading>
        <Paragraph center>
          Vi har lang erfaring når det kommer til boligsalg i området, og vi vet
          hva som skal til for å oppnå en høyest mulig pris. Med bransjeledende
          produkter og innovative løsninger, gir vi deg et fortrinn i
          eiendomsmarkedet. Om du skal kjøpe eller selge, tilbyr vi alltid en
          skreddersydd strategi, plan og gjennomføring tilpasset dine behov.
        </Paragraph>
        <Paragraph center>
          Hele 9 av 10 som velger å selge boligen sin gjennom oss, gjør det på
          anbefaling fra andre som har gjort det samme. Det er det beste beviset
          vi har på at våre meglere leverer en kundeopplevelse og resultater som
          overgår deres forventninger.
        </Paragraph>
        <Paragraph center>
          Vi er din lokale megler - og du finner oss på Holtet.
        </Paragraph>

        <FormWrapper style={fadeOut}>
          <Section className="form-section" style={{ paddingBottom: '10px' }}>
            <Heading tag="h2" center={true}>
              Kontakt oss
            </Heading>
          </Section>
          <Form onSubmit={handleSubmit} noValidate>
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}
            {fields.map((item: any, index: number) => {
              if (item.type === 'textarea') {
                return (
                  <FormRow key={`field_${index}`}>
                    <Textarea
                      name={item.name}
                      label={item.label}
                      placeholder={item.placeholder}
                      value={item.value}
                      onChange={handleChange}
                      error={item.error}
                    />
                  </FormRow>
                );
              }

              return (
                <FormRow key={`field_${index}`}>
                  <Input
                    type={item.type}
                    name={item.name}
                    label={item.label}
                    labelAfter={item.labelAfter}
                    placeholder={item.placeholder}
                    value={item.value}
                    onChange={handleChange}
                    error={item.error}
                  />
                </FormRow>
              );
            })}
            <ButtonGroup>
              <Button
                type="primary"
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Send
              </Button>
              <ButtonLink
                as="a"
                href={routeConfig.personvern.url}
                className="privacy-link-mobile"
              >
                Personvernpolicy
              </ButtonLink>
            </ButtonGroup>
          </Form>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
          </Section>
        </Thanks>
      </Container>
      <BackgroundLoad
        opacity={0.3}
        url="https://cdn.reeltime.no/pm_assets/kampanjer/sandnes/Me_kjenne_Sandnes_toppbilde_Gradient.jpg"
      />
    </>
  );
};

export default Fremsnakk;

const FormWrapper = styled(animated.div)``;

const HeroContent = styled(animated.div)`
  ${BaseHeroContent}
  flex-flow: column;
  max-width: 960px;
`;
